import React, { useState, useEffect } from "react";
import {
  Alert,
  Grid,
  Typography,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Paper,
  CircularProgress,
  Box,
  useTheme,
} from "@mui/material";
import parse from "html-react-parser";
import { toast } from "react-toastify";
import Moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import { DashboardApi } from "../../services/api/misc";
import { getContrastYIQ, neutralTierBg } from "../../utils/colorUtils";

Moment.locale("en");

function DashboardCenter(props) {
  const theme = useTheme();
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [activityList, setActivityList] = useState([]);
  const [distributionList, setDistributionList] = useState([]);
  const [collectionList, setCollectionList] = useState([]);
  const [tierList, setTierList] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [dismissAlert, setDismissAlert] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    
    const getData = async () => {
      try {
        let token = props.token;
        if (typeof token === 'function') {
          token = await token();
        }
        if (!token || typeof token !== 'string' || token === 'undefined') {
          toast.error('No valid token found for collections API call.');
          return;
        }
        const response = await DashboardApi.getDashboard(token);

        if (response.success) {
          const responseData = response.data;
          let tierData = {
            0: {
              color: null,
              parentTier: "0",
              relId: 0,
              tierName: "No Tier",
            },
          };

          responseData.tierlist.forEach((item) => {
            tierData[String(item.relId)] = {
              color: item.color,
              parentTier: item.parentTier,
              relId: item.relId,
              tierName: item.tierName,
            };
          });

          responseData.collectionlist.forEach((item) => {
            item.relId = Number(item.relId);
          });

          responseData.distributionlist.forEach((item) => {
            if (item.distributionStatus === "SCHEDULED") {
              item.startTime = ` on ${Moment.unix(item.startTime).format("YYYY/MM/DD hh:mm a")}`;
              item.badge = "warning";
            } else {
              item.badge = "success";
              item.startTime = "";
            }
          });

          // Store with user ID to ensure user-specific caching
          // SS-242: Strip additive metrics from cache — consumer doesn't render
          // metrics, and totalEarnings is financially sensitive (Security audit).
          const cacheKey = `dashboard_${props.user?.sub || 'unknown'}`;
          const tierCacheKey = `tierList_${props.user?.sub || 'unknown'}`;

          const { metrics, ...cacheableData } = responseData;
          localStorage.setItem(cacheKey, JSON.stringify(cacheableData));
          localStorage.setItem(tierCacheKey, JSON.stringify(tierData));

          if (isMounted) {
            setActivityList(responseData.activitylist);
            setDistributionList(responseData.distributionlist);
            setCollectionList(responseData.collectionlist);
            setTierList(tierData);
            setIsLoading(false);
          }
        } else {
          toast.error(`Dashboard Endpoint: Error ${response.status}`);
        }
      } catch (err) {
        toast.error(`Uh oh: Looks like something went wrong. ${err}`);
        if (isMounted) setIsLoading(false);
      }
    };

    // Check for cached data with user-specific key
    const cacheKey = `dashboard_${props.user?.sub || 'unknown'}`;
    const tierCacheKey = `tierList_${props.user?.sub || 'unknown'}`;
    
    if (localStorage.getItem(cacheKey) && props.user?.sub) {
      const dashboard = JSON.parse(localStorage.getItem(cacheKey));
      setActivityList(dashboard.activitylist);
      setDistributionList(dashboard.distributionlist);
      setCollectionList(dashboard.collectionlist);
      setTierList(JSON.parse(localStorage.getItem(tierCacheKey)) || {});
      setIsLoading(false);
    }

    // Always fetch fresh data to ensure it's current
    getData();

    return () => {
      isMounted = false;
    };
  }, [props.token, props.user?.sub]); // Add user.sub as dependency

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (isLoading) {
    return <CircularProgress sx={{ mt: 4 }} />;
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 6, md: 4 } }}> {/* Enhanced bottom padding for footer spacing */}
      {showAlert && (
        <Alert severity={alertType} sx={{ textAlign: "left", mx: 3, mb: 2 }}>
          {alertMessage}
        </Alert>
      )}

      {!props.user.email_verified && !dismissAlert && (
        <Alert
          severity="info"
          onClose={() => setDismissAlert(true)}
          sx={{ 
            textAlign: "left",
            mb: 2,
            mx: 3
          }}
        >
          Your email is not yet verified. Please check your inbox and verify it to ensure account security.
        </Alert>
      )}

      <Box sx={{ mb: 2, textAlign: "right", pr: 2, mx: 2 }}>
        {props.isAdmin && (
          <>
            <Button
              variant="contained"
              color="error"
              sx={{ m: 1 }}
              onClick={() => {
                localStorage.setItem("adminMode", "true");
                window.location.reload(false);
              }}
            >
              Toggle Admin Mode
            </Button>
            <Button component={Link} to="/collections" sx={{ m: 1 }} variant="contained">
              View Sets
            </Button>
          </>
        )}

        <Button
          variant="contained"
          onClick={handleMenuClick}
          sx={{ m: 1 }}
        >
          Quick Add
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 160
            }
          }}
        >
          <MenuItem component={Link} to="/tiers/add/" onClick={handleMenuClose}>
            Tier
          </MenuItem>
          <MenuItem component={Link} to="/collections/add/" onClick={handleMenuClose}>
            Add Set
          </MenuItem>
          <MenuItem component={Link} to="/distributions/add/" onClick={handleMenuClose}>
            Distribution
          </MenuItem>
        </Menu>
      </Box>

      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, mx: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}>
                Recent Distributions
              </Typography>
              {distributionList.length > 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <List disablePadding>
                    {distributionList.map((item, index) => (
                      <ListItemButton
                        key={index}
                        onClick={() => navigate(`/distributions/${item.relId || item.id}`)}
                        sx={{ 
                          py: 2,
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          },
                          borderBottom: index < distributionList.length - 1 ? '1px solid' : 'none',
                          borderBottomColor: 'divider'
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {item.subject}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary" component="div">
                              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={item.distributionStatus}
                                  color={item.badge}
                                  size="small"
                                  sx={{ fontWeight: 500 }}
                                />
                                {item.startTime && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.startTime}
                                  </Typography>
                                )}
                              </Box>
                            </Typography>
                          }
                        />
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {Moment.unix(item.createdAt).format("LLL")}
                        </Typography>
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 6, 
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: 'divider'
                }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No distributions yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first distribution to get started
                  </Typography>
                  <Button 
                    component={Link} 
                    to="/distributions/add/"
                    variant="contained"
                    size="small"
                  >
                    Create Distribution
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}>
                Recent Collections
              </Typography>
              {collectionList.length > 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <List disablePadding>
                    {collectionList.map((collection, index) => (
                      <ListItemButton
                        key={collection.relId}
                        onClick={() => navigate(`/collections/${collection.relId}`)}
                        sx={{ 
                          py: 2,
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          },
                          borderBottom: index < collectionList.length - 1 ? '1px solid' : 'none',
                          borderBottomColor: 'divider'
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {collection.collectionName}
                            </Typography>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={tierList[collection.tier]?.tierName || "No Tier"}
                                sx={{
                                  backgroundColor: tierList[collection.tier]?.color || neutralTierBg(theme.palette.mode),
                                  color: getContrastYIQ(tierList[collection.tier]?.color || neutralTierBg(theme.palette.mode)),
                                  fontWeight: 500
                                }}
                                size="small"
                              />
                            </Box>
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          {Moment.unix(collection.createdAt).format("LLL")}
                        </Typography>
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 6, 
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: 'divider'
                }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No collections yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Upload your first collection to get started
                  </Typography>
                  <Button 
                    component={Link} 
                    to="/collections/add/"
                    variant="contained"
                    size="small"
                    className="mt-md-3 mt-2"
                  >
                    Add Set
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}>
            Recent Activity
          </Typography>
          {activityList.length > 0 ? (
            <Paper
              variant="outlined"
              sx={{ 
                maxHeight: "400px", 
                overflowY: "auto", 
                borderRadius: 2
              }}
            >
              <List disablePadding>
                {activityList.map((item, index) => (
                  <ListItem 
                    key={index} 
                    sx={{ 
                      py: 2,
                      flexDirection: "column", 
                      alignItems: "flex-start",
                      borderBottom: index < activityList.length - 1 ? '1px solid' : 'none',
                      borderBottomColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {item.activity}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Moment.unix(item.createdAt).format("LLL") === "Invalid date"
                          ? item.createdAt
                          : Moment.unix(item.createdAt).format("LLL")}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {parse(item.remarks)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6, 
              backgroundColor: 'background.paper',
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'divider'
            }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No activity yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Activity will appear here as you use the platform
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default DashboardCenter;
